/*
 *
 * TutorialPopup
 *
 * @version 1.0.0
 * @author Giorgia Cuozzo
 * @since 10-Sep-2025
 */

import React, { useState, useEffect, useRef } from 'react'
import PropTypes from 'prop-types'
import Dialog from '@mui/material/Dialog'
import DialogActions from '@mui/material/DialogActions'
import DialogContent from '@mui/material/DialogContent'
import PortalButton from '@nexidigital/portalbutton'
import './TutorialPopup.scss'
import formattedMessage from './languageModule'
import { nullSafe } from 'Utility/globalMethods'
import { getEligibleTutorialSteps, TUTORIALS } from './TutorialPopupUtils'
import { Capacitor } from '@capacitor/core'

// Le immagini sono sul CMS al seguente path:
//   NEXI:
//     nexi/business/images/tutorial_hp.png
//     nexi/business/images/tutorial_transactions.png
//     nexi/business/images/tutorial_results.png
//     nexi/business/images/tutorial_documents.png
//     nexi/business/images/tutorial_menu.png
//     nexi/business/images/tutorial_commission.png
//     nexi/business/images/tutorial_services.png
//   UCI:
//     nexi/business/uci/images/tutorial_hp.png
//     nexi/business/uci/images/tutorial_transactions.png
//     nexi/business/uci/images/tutorial_results.png
//     nexi/business/uci/images/tutorial_documents.png
//     nexi/business/uci/images/tutorial_menu.png

const TutorialPopup = ({
  title,
  primaryButtonLabel,
  secondaryButtonTertiary,
  secondaryButtonLabel,
  disableSendAnalytics,
  handleAnalytics,
  closeLabel,
  customAnalyticsDescription,
  ...props
}) => {
  const isNexiTheme = document.documentElement.getAttribute('data-theme') === 'nexi'

  const [tutorialSteps, setTutorialSteps] = useState([])
  const [tutorialIndex, setTutorialIndex] = useState(0)
  const [hasUpdatedOnce, setHasUpdatedOnce] = useState(false)
  const contentRef = useRef(null)

  // Build tutorial steps by eligibility and open on first access (only mobile)
  const cluster = nullSafe(() => props.clusterType, '')
  const dbSourceType = nullSafe(() => props.dbSourceType, [])
  const { steps: eligibleSteps } = getEligibleTutorialSteps({
    tutorialKey: TUTORIALS.HOMEPAGE_TUTORIAL,
    cluster,
    dbSourceType
  })

  const popupAnalytics = (clicked) => {
    if (disableSendAnalytics) return

    if (handleAnalytics) {
      handleAnalytics(clicked)
    } else {
      const globalEvent = new CustomEvent('modalAnalytics', {
        detail: {
          modalTitle: title || '',
          modalDescription: customAnalyticsDescription || '',
          modalPrimaryButtonLabel: primaryButtonLabel,
          modalSecondaryButtonLabel: secondaryButtonLabel || '',
          clicked,
          modalCloseButtonLabel: closeLabel
        }
      })
      window.dispatchEvent(globalEvent)
    }
  }

  const closeTutorial = () => {
    if (!hasUpdatedOnce) {
      props.actions.doUpdateTutorial({ tutorial: 'HOMEPAGE_TUTORIAL_VISIBLE' })
      setHasUpdatedOnce(true)
    }
    // Hide TTL only after the tutorial popup is closed
    props.actions.doHideTtl()
    props.actions.doRetrieveTTL()
  }

  const nextTutorial = () => {
    if (tutorialIndex < tutorialSteps.length - 1) {
      setTutorialIndex((i) => i + 1)
    } else {
      // Last step: update tutorial status and close
      closeTutorial()
    }
  }

  const prevTutorial = () => {
    if (tutorialIndex > 0) setTutorialIndex((i) => i - 1)
  }

  useEffect(() => {
    popupAnalytics(false)
  }, [])

  useEffect(() => {
    setTutorialSteps(eligibleSteps)
    setTutorialIndex(0)
    // If we can evaluate eligibility inputs and there are no steps, hide TTL
    const eligibilityInputsReady = !!cluster || (Array.isArray(dbSourceType) && dbSourceType.length > 0)
    if (eligibilityInputsReady && eligibleSteps.length === 0) dontShowTutorial()
  }, [cluster, dbSourceType])

  useEffect(() => {
    // If userRole is known and not ADMIN, hide TTL; otherwise wait for role
    if (!!props.userRole && props.userRole !== 'ADMIN') dontShowTutorial()
  }, [props.userRole])

  // Trigger update when tutorial actually becomes visible the first time
  useEffect(() => {
    if (
      Capacitor.isNativePlatform() && props.userRole === 'ADMIN' && tutorialSteps.length > 0 && !hasUpdatedOnce
    ) {
      props.actions.doUpdateTutorial({ tutorial: 'HOMEPAGE_TUTORIAL_VISIBLE' })
      setHasUpdatedOnce(true)
    }
  }, [tutorialSteps, props.userRole])

  const handlePrimaryButtonClick = () => {
    popupAnalytics(primaryButtonLabel)
    nextTutorial()
  }

  const handleSecondaryButtonClick = () => {
    popupAnalytics(secondaryButtonLabel)
    prevTutorial()
  }

  const handleCloseButtonClick = () => {
    popupAnalytics(closeLabel)
    closeTutorial()
  }

  const handleSkipButtonClick = () => {
    popupAnalytics(formattedMessage.stepTutorialSkip)
    closeTutorial()
  }

  const dontShowTutorial = () => {
    props.actions.doHideTtl()
    props.actions.doRetrieveTTL()
  }

  if (!Capacitor.isNativePlatform()) {
    dontShowTutorial()
    return null
  }

  // Visible only if on app, steps exist and user is ADMIN
  const isVisible = Capacitor.isNativePlatform() && tutorialSteps.length > 0 && props.userRole === 'ADMIN'

  // Ensure the page and dialog content start from the top when shown or when step changes
  const scrollToTop = () => {
    // Scroll underlying page to top (in case background was scrolled)
    try {
      window.scrollTo({ top: 0, left: 0, behavior: 'auto' })
    } catch (e) {
      window.scrollTo(0, 0)
    }
    // Scroll dialog content to top (DialogContent is scrollable)
    if (contentRef.current) {
      try {
        contentRef.current.scrollTo({ top: 0, left: 0, behavior: 'auto' })
      } catch (e) {
        contentRef.current.scrollTop = 0
      }
    }
  }

  // On first visibility and on every tutorial page change, force scroll to top
  useEffect(() => {
    if (isVisible) scrollToTop()
  }, [isVisible, tutorialIndex])

  if (isVisible) {
    return (
      <Dialog
        open
        onClose={handleCloseButtonClick}
        maxWidth='sm'
        fullWidth
        className={`tutorialModal ${isNexiTheme ? 'darkBg' : ''}`}
      >
        <div tabIndex={-1} role='dialog' />
        <div className='barTitle'>
          {tutorialIndex !== tutorialSteps.length - 1 &&
            <PortalButton
              textButton
              onClick={handleSkipButtonClick}
              ariaLabel={formattedMessage.stepTutorialSkip}
              label={formattedMessage.stepTutorialSkip}
              size='small'
              invert={isNexiTheme}
            />}
        </div>
        <DialogContent className='tutorialContent' ref={contentRef}>
          <div>
            <img src={tutorialSteps[tutorialIndex].img} alt={tutorialSteps[tutorialIndex].key} className='tutorialImg' />
            <h2 className={`tutorialTitle ${isNexiTheme ? 'darkTitle' : ''}`}>{tutorialSteps[tutorialIndex].title}</h2>
          </div>
        </DialogContent>
        <DialogActions className='tutorialModalButtons'>
          {tutorialIndex > 0 && (
            <PortalButton
              id='modalSecondaryCta'
              label={formattedMessage.stepTutorialPrev}
              secondary={!secondaryButtonTertiary}
              onClick={() => handleSecondaryButtonClick()}
              invert={isNexiTheme}
            />
          )}
          <PortalButton
            id='modalPrimaryCta'
            label={
              tutorialIndex < tutorialSteps.length - 1
                ? formattedMessage.stepTutorialNext
                : formattedMessage.stepTutorialGoHome
            }
            onClick={() => handlePrimaryButtonClick()}
            invert={isNexiTheme}
          />
        </DialogActions>
      </Dialog>
    )
  }
  return null
}

TutorialPopup.propTypes = {
  actions: PropTypes.object,
  title: PropTypes.string,
  primaryButtonLabel: PropTypes.string,
  secondaryButtonTertiary: PropTypes.bool,
  secondaryButtonLabel: PropTypes.string,
  disableSendAnalytics: PropTypes.bool,
  handleAnalytics: PropTypes.func,
  closeLabel: PropTypes.string,
  customAnalyticsDescription: PropTypes.string,
  clusterType: PropTypes.string,
  dbSourceType: PropTypes.array,
  userRole: PropTypes.string
}

TutorialPopup.defaultProps = {
  actions: {},
  title: '',
  primaryButtonLabel: '',
  secondaryButtonTertiary: false,
  secondaryButtonLabel: '',
  disableSendAnalytics: false,
  handleAnalytics: null,
  closeLabel: '',
  customAnalyticsDescription: '',
  clusterType: '',
  dbSourceType: [],
  userRole: ''
}

export default TutorialPopup
